// Browser entry for the preview. Imports the SAME engine core the Node
// demo uses (core/lighting.ts, core/paintPlan.ts, core/floorPlan.ts,
// core/style.ts), so the reasoning and insights shown here are the ones the
// engine produces. esbuild bundles this to app.js; nothing here is
// preview-specific except the default room geometry and the DOM rendering.

import { planLighting, cctLabel, type LightingInput, type LightLayerKind } from "../src/core/lighting.js";
import { planWallColor, type PaintPlanInput } from "../src/core/paintPlan.js";
import { planFloor, roomFloors, type Floor, type FloorCategory } from "../src/core/floorPlan.js";
import { planHardware } from "../src/core/hardwarePlan.js";
import { effectiveTemperature } from "../src/core/color.js";
import {
  styleInsight,
  type StyleKey,
  type StyleInsight,
} from "../src/core/style.js";
import { renderInsight } from "../src/core/tone.js";
import { coherence, renderCoherence, VERDICT_COPY, type FinishItem, type CoherenceReport } from "../src/core/coherence.js";
import { computeDesignScore } from "../src/core/designScore.js";
import {
  cctTip, brightnessTip, fixtureTip, lrvTip, sheenTip, floorLrvTip, type Tip,
} from "../src/core/guidance.js";
import { STYLES } from "../src/core/style.js";
import { searchCatalog, type CatalogResult } from "../src/core/catalog.js";
import { sourcesFor, type Category } from "../src/data/sources.js";
import type { RoomType, Orientation, DaylightLevel, Objective, Tone } from "../src/core/types.js";

interface RoomPreset {
  key: string;
  roomType: RoomType;
  label: string;
  length: number; // ft
  width: number; // ft
  ceiling: number;
  orient: Orientation;
  daylight: DaylightLevel;
}

const ROOMS: RoomPreset[] = [
  { key: "kitchen", roomType: "kitchen", label: "Kitchen", length: 14, width: 10, ceiling: 8, orient: "north", daylight: "low" },
  { key: "dining", roomType: "dining", label: "Dining", length: 15, width: 12, ceiling: 9, orient: "south", daylight: "medium" },
  { key: "living", roomType: "living", label: "Living", length: 20, width: 15, ceiling: 9, orient: "west", daylight: "medium" },
  { key: "bedroom", roomType: "bedroom", label: "Primary Bedroom", length: 16, width: 12.5, ceiling: 9, orient: "east", daylight: "medium" },
  { key: "bath", roomType: "bath", label: "Bathroom", length: 10, width: 6, ceiling: 8, orient: "north", daylight: "low" },
];

const LAYER_COLOR: Record<LightLayerKind, string> = {
  ambient: "var(--sage)", task: "var(--coral)", accent: "var(--coral)", feature: "var(--coral)",
};

let current = "kitchen";
const el = (id: string) => document.getElementById(id) as HTMLElement;
const val = (id: string) => (document.getElementById(id) as HTMLInputElement | HTMLSelectElement).value;

// Context the floor picker needs to recompute an insight without a full
// re-render (so changing only the floor keeps everything else stable).
interface FloorCtx { orient: Orientation; cct: number; style: StyleKey; tone: Tone; floors: Floor[]; }
let floorCtx: FloorCtx = { orient: "north", cct: 3500, style: "scandi", tone: "professional", floors: [] };

// Real-life clutter the user can stack on top of the recommended picks to
// stress-test coherence. Each contributes a metal family, a wood tone, or a
// loud element, so metals, wood tones, and busyness all become reachable.
const CLUTTER: { id: string; label: string; item: FinishItem }[] = [
  { id: "c-brass", label: "Brass faucet", item: { role: "faucet", label: "Brass faucet", metalFamily: "warm" } },
  { id: "c-chrome", label: "Chrome pulls", item: { role: "pulls", label: "Chrome pulls", metalFamily: "cool" } },
  { id: "c-black", label: "Matte black lighting", item: { role: "lighting", label: "Matte black pendant", metalFamily: "neutral" } },
  { id: "c-walnut", label: "Walnut console", item: { role: "console", label: "Walnut console", woodTone: { label: "walnut", temperature: 0.35 } } },
  { id: "c-gray", label: "Gray-washed shelf", item: { role: "shelf", label: "Gray-washed shelf", woodTone: { label: "gray oak", temperature: -0.2 } } },
  { id: "c-rug", label: "Bold patterned rug", item: { role: "rug", label: "Bold patterned rug", chroma: 0.7, areaWeight: 0.4 } },
  { id: "c-gallery", label: "Busy gallery wall", item: { role: "art", label: "Busy gallery wall", chroma: 0.6, areaWeight: 0.3 } },
];
// Base finishes from the recommended picks + current tone, refreshed by render().
let cohCtx: { base: FinishItem[]; tone: Tone } = { base: [], tone: "professional" };

// User overrides pulled from the vendor catalog search. Cleared when the
// room changes. render() applies these on top of the recommendation.
let picks: { wall?: CatalogResult; floor?: CatalogResult } = {};

// The first selectable engine factor: the light color temperature. null
// means "use the room's recommended CCT"; a number is the user's override
// (from the Kelvin slider), which flows into the paint color math. Cleared
// when the room changes. Brightness and fixture count can follow this same
// override pattern next.
let cctOverride: number | null = null;
// The next two selectable factors, same pattern: brightness feeds the lumen
// method, fixture count re-lays-out the grid. null = use the recommendation.
let fcOverride: number | null = null;
let countOverride: number | null = null;
// Paint factors: a target lightness (LRV) steers the color pick; a sheen
// choice overrides the computed sheen. null = use the recommendation.
let lrvOverride: number | null = null;
let sheenOverride: string | null = null;
// Floor factors: a target lightness and a material-family filter.
let floorLrvOverride: number | null = null;
let floorCatOverride: string | null = null;

interface SpectrumOption { value: number | string; label: string; sub: string; }
interface SpectrumDefinition {
  id: string; label: string; help: string; options: SpectrumOption[];
  current: () => number | string; recommended: () => number | string;
  select: (value: number | string) => void;
}

function nearestOption(options: SpectrumOption[], current: number | string): SpectrumOption {
  if (typeof current === "string") return options.find((o) => o.value === current) ?? options[0];
  return [...options].sort((a, b) => Math.abs(Number(a.value) - current) - Math.abs(Number(b.value) - current))[0];
}

function resetDesignLevers() {
  cctOverride = fcOverride = countOverride = lrvOverride = floorLrvOverride = null;
  sheenOverride = floorCatOverride = null;
  picks = {};
  (el("pull") as HTMLSelectElement).value = "0.25";
  render();
}

function renderDesignLab(
  L: ReturnType<typeof planLighting>,
  P: ReturnType<typeof planWallColor>,
  F: ReturnType<typeof planFloor>,
  baselineP: ReturnType<typeof planWallColor>,
  baselineF: ReturnType<typeof planFloor>,
) {
  const defs: SpectrumDefinition[] = [
    {
      id: "temperature", label: "Light temperature", help: "cozy ↔ crisp",
      options: [
        { value: 2700, label: "2700K", sub: "Cozy" }, { value: 3000, label: "3000K", sub: "Soft" },
        { value: 3500, label: "3500K", sub: "Balanced" }, { value: 4000, label: "4000K", sub: "Crisp" },
        { value: 5000, label: "5000K", sub: "Daylight" },
      ],
      current: () => cctOverride ?? L.cct, recommended: () => L.cct,
      select: (v) => { cctOverride = Number(v); render(); },
    },
    {
      id: "brightness", label: "Room brightness", help: "relaxed ↔ task-ready",
      options: [
        { value: 15, label: "15 fc", sub: "Relaxed" }, { value: 25, label: "25 fc", sub: "Ambient" },
        { value: 35, label: "35 fc", sub: "Balanced" }, { value: 45, label: "45 fc", sub: "Task" },
        { value: 55, label: "55 fc", sub: "Bright" },
      ],
      current: () => fcOverride ?? L.targetFc, recommended: () => L.recommendedTargetFc,
      select: (v) => { fcOverride = Number(v); render(); },
    },
    {
      id: "wall-lrv", label: "Wall lightness", help: "moody ↔ airy",
      options: [
        { value: 20, label: "LRV 20", sub: "Moody" }, { value: 40, label: "LRV 40", sub: "Grounded" },
        { value: 60, label: "LRV 60", sub: "Balanced" }, { value: 80, label: "LRV 80", sub: "Airy" },
        { value: 90, label: "LRV 90", sub: "Bright white" },
      ],
      current: () => picks.wall?.lrv ?? lrvOverride ?? P.wall.lrv, recommended: () => baselineP.wall.lrv,
      select: (v) => { lrvOverride = Number(v); picks.wall = undefined; render(); },
    },
    {
      id: "floor-lrv", label: "Floor lightness", help: "deep ↔ pale",
      options: [
        { value: 20, label: "LRV 20", sub: "Deep" }, { value: 35, label: "LRV 35", sub: "Rich" },
        { value: 50, label: "LRV 50", sub: "Mid-tone" }, { value: 65, label: "LRV 65", sub: "Light" },
        { value: 75, label: "LRV 75", sub: "Pale" },
      ],
      current: () => picks.floor?.lrv ?? floorLrvOverride ?? F.top.lrv, recommended: () => baselineF.top.lrv,
      select: (v) => { floorLrvOverride = Number(v); picks.floor = undefined; render(); },
    },
    {
      id: "sheen", label: "Wall finish", help: "soft ↔ washable",
      options: [
        { value: "flat", label: "Flat", sub: "Softest" }, { value: "matte", label: "Matte", sub: "Quiet" },
        { value: "eggshell", label: "Eggshell", sub: "Balanced" }, { value: "satin", label: "Satin", sub: "Washable" },
        { value: "semi-gloss", label: "Semi-gloss", sub: "Durable" },
      ],
      current: () => sheenOverride ?? P.wallSheen, recommended: () => baselineP.recommendedSheen,
      select: (v) => { sheenOverride = String(v); render(); },
    },
    {
      id: "style-pull", label: "Style influence", help: "principles ↔ personality",
      options: [
        { value: "0.15", label: "15%", sub: "Principles" }, { value: "0.25", label: "25%", sub: "Balanced" },
        { value: "0.4", label: "40%", sub: "Style-led" },
      ],
      current: () => val("pull"), recommended: () => "0.25",
      select: (v) => { (el("pull") as HTMLSelectElement).value = String(v); render(); },
    },
  ];

  const box = el("spectrums");
  box.innerHTML = "";
  for (const d of defs) {
    const row = document.createElement("div"); row.className = "spectrum"; row.dataset.spectrum = d.id;
    const name = document.createElement("div"); name.className = "spectrum-name";
    name.innerHTML = `${d.label}<small>${d.help}</small>`;
    const opts = document.createElement("div"); opts.className = "spectrum-options";
    const active = nearestOption(d.options, d.current());
    const recommended = nearestOption(d.options, d.recommended());
    for (const o of d.options) {
      const b = document.createElement("button"); b.type = "button";
      b.className = (o === active ? "active " : "") + (o === recommended ? "recommended" : "");
      b.innerHTML = `<b>${o.label}</b>${o.sub}`;
      b.onclick = () => d.select(o.value);
      opts.appendChild(b);
    }
    row.appendChild(name); row.appendChild(opts); box.appendChild(row);
  }

  const changed = [cctOverride, fcOverride, countOverride, lrvOverride, floorLrvOverride, sheenOverride, floorCatOverride]
    .filter((v) => v !== null).length + (picks.wall ? 1 : 0) + (picks.floor ? 1 : 0) + (val("pull") !== "0.25" ? 1 : 0);
  el("lab-status").textContent = changed ? `${changed} design lever${changed === 1 ? "" : "s"} adjusted` : "Using the room recommendations";
  el("app-project").textContent = `${preset().label} · ${changed ? "edited" : "working plan"}`;
}

function roomViz(cols: number, rows: number, spacing: number, offset: number): string {
  const W = 100, H = 64, pad = 8;
  const gx = (W - 2 * pad) / (cols + (2 * offset) / spacing);
  const gy = (H - 2 * pad) / (rows + (2 * offset) / spacing);
  let dots = "";
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = pad + (offset / spacing) * gx + c * gx + (gx / 2) * (cols > 1 ? 0 : 1);
      const y = pad + (offset / spacing) * gy + r * gy + (gy / 2) * (rows > 1 ? 0 : 1);
      dots +=
        `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2.4" fill="#F5F0E8"/>` +
        `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="5.2" fill="none" stroke="#7A8C6E" stroke-width="0.7" opacity="0.7"/>`;
    }
  }
  return `<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="Recessed light layout">
    <rect x="2" y="2" width="${W - 4}" height="${H - 4}" rx="4" fill="#26251f" stroke="#4A4740"/>
    ${dots}
    <text x="6" y="${H - 6}" fill="#A9A296" font-size="4.5" font-family="Poppins,sans-serif">${cols} x ${rows} grid, ${spacing} ft on center</text>
  </svg>`;
}

function renderTabs() {
  const t = el("tabs");
  t.innerHTML = "";
  for (const r of ROOMS) {
    const b = document.createElement("button");
    b.className = "tab" + (r.key === current ? " active" : "");
    b.textContent = r.label;
    b.onclick = () => {
      current = r.key; picks = {};
      cctOverride = fcOverride = countOverride = lrvOverride = floorLrvOverride = null;
      sheenOverride = floorCatOverride = null;
      syncControls(); render();
    };
    t.appendChild(b);
  }
}

function preset(): RoomPreset {
  return ROOMS.find((r) => r.key === current) ?? ROOMS[0];
}

function syncControls() {
  const r = preset();
  (el("length") as HTMLInputElement).value = String(r.length);
  (el("width") as HTMLInputElement).value = String(r.width);
  (el("ceiling") as HTMLInputElement).value = String(r.ceiling);
  (el("orient") as HTMLInputElement).value = r.orient;
  (el("daylight") as HTMLInputElement).value = r.daylight;
}

function bullets(target: string, lines: string[]) {
  el(target).innerHTML = "";
  for (const line of lines) {
    const li = document.createElement("li");
    li.textContent = line;
    el(target).appendChild(li);
  }
}

// Render an insight chip in the chosen tone, or hide it when the pick fits
// the vibe.
function showInsight(id: string, insight: StyleInsight | null, tone: Tone) {
  const node = el(id);
  if (!insight) {
    node.className = "insight hidden";
    node.textContent = "";
    return;
  }
  node.className = "insight " + insight.severity;
  const sev = document.createElement("span");
  sev.className = "sev";
  sev.textContent = `${insight.severity} style note`;
  node.textContent = renderInsight(insight, tone);
  node.prepend(sev);
}

function renderFloorSelection(name: string) {
  const opt = floorCtx.floors.find((f) => f.name === name) ?? floorCtx.floors[0];
  if (!opt) return;
  (el("f-sel-sw") as HTMLElement).style.background = opt.hex;
  const effTemp = effectiveTemperature(opt.temperature, floorCtx.orient, floorCtx.cct);
  const insight = styleInsight(
    { name: opt.name, lrv: opt.lrv, temperature: effTemp, chroma: opt.chroma_opinion },
    floorCtx.style, "floor"
  );
  showInsight("f-insight", insight, floorCtx.tone);
  el("f-fit").textContent = insight ? "" : "On brief for this vibe. No flag needed.";
}

// Build the clutter toggle chips once; each recomputes the report on change.
function initCoherenceToggles() {
  const box = el("c-toggles");
  box.innerHTML = "";
  for (const c of CLUTTER) {
    const label = document.createElement("label");
    label.className = "toggle";
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.id = c.id;
    cb.addEventListener("change", () => {
      label.classList.toggle("on", cb.checked);
      renderCoherenceCard();
    });
    label.appendChild(cb);
    label.appendChild(document.createTextNode(c.label));
    box.appendChild(label);
  }
}

const VERDICT_CHIP: Record<CoherenceReport["verdict"], string> = {
  clean: "green", busy: "amber", chaotic: "red",
};

// Everything the Design Score needs that isn't the coherence report itself.
// Set in render(); the score is recomputed whenever coherence is, so the
// clutter toggles move the score too.
interface ScoreCtx {
  styleVibe: StyleKey; styleLabel: string;
  wall: { name: string; lrv: number; effTemp: number; chroma: number };
  floor: { name: string; lrv: number; effTemp: number; chroma: number };
  harmony: { gap: number; score: number } | null;
  lrvFloor: number; wallMeetsLrvFloor: boolean;
  sheenBelowWetFloor: boolean; durabilityOk: boolean;
}
let scoreCtx: ScoreCtx | null = null;

const BAND_CHIP: Record<string, string> = {
  excellent: "var(--sage)", strong: "var(--sage)", mixed: "var(--amber)", conflicted: "var(--red)",
};

function renderDesignScore(report: CoherenceReport) {
  if (!scoreCtx) return;
  const s = computeDesignScore({ ...scoreCtx, coherence: report });

  el("ds-total").textContent = String(s.total);
  (el("ds-total") as HTMLElement).style.color = BAND_CHIP[s.band];
  el("ds-band").textContent = s.band.charAt(0).toUpperCase() + s.band.slice(1);
  el("ds-headline").textContent = s.headline;

  const box = el("ds-bars");
  box.innerHTML = "";
  for (const c of s.components) {
    const pct = Math.round(c.score * 100);
    const color = pct >= 80 ? "var(--sage)" : pct >= 55 ? "var(--amber)" : "var(--red)";
    const row = document.createElement("div");
    row.className = "sbar";
    const top = document.createElement("div");
    top.className = "top";
    const lbl = document.createElement("span");
    lbl.className = "lbl";
    lbl.textContent = c.label;
    const p = document.createElement("span");
    p.className = "pct";
    p.textContent = pct + "%";
    top.appendChild(lbl); top.appendChild(p);
    const track = document.createElement("div");
    track.className = "track";
    const fill = document.createElement("div");
    fill.className = "fill";
    fill.style.width = pct + "%";
    fill.style.background = color;
    track.appendChild(fill);
    const det = document.createElement("div");
    det.className = "det";
    det.textContent = c.detail;
    row.appendChild(top); row.appendChild(track); row.appendChild(det);
    box.appendChild(row);
  }
}

function renderCoherenceCard() {
  const active = CLUTTER.filter((c) => (document.getElementById(c.id) as HTMLInputElement)?.checked).map((c) => c.item);
  const report = coherence([...cohCtx.base, ...active]);
  renderDesignScore(report);

  el("c-verdict").textContent = VERDICT_COPY[report.verdict];
  el("c-verdict-label").textContent = report.verdict.charAt(0).toUpperCase() + report.verdict.slice(1);
  (el("c-chip") as HTMLElement).className = "chip " + VERDICT_CHIP[report.verdict];

  const box = el("c-findings");
  box.innerHTML = "";
  for (const f of report.findings) {
    const row = document.createElement("div");
    row.className = "finding " + f.severity;
    const rule = document.createElement("span");
    rule.className = "rule";
    rule.textContent = f.rule;
    const text = document.createElement("span");
    text.textContent = renderCoherence(f, cohCtx.tone);
    row.appendChild(rule);
    row.appendChild(text);
    box.appendChild(row);
  }
}

// Show which vetted sources back the current category, connected vs planned.
function renderSourceLegend(type: Category) {
  const box = el("cat-sources");
  box.innerHTML = "";
  for (const s of sourcesFor(type)) {
    const chip = document.createElement("span");
    chip.className = "src";
    const dot = document.createElement("span");
    dot.className = "d " + (s.status === "connected" ? "on" : "off");
    chip.appendChild(dot);
    chip.appendChild(document.createTextNode(`${s.name} · T${s.tier}`));
    box.appendChild(chip);
  }
}

function runSearch() {
  const type = val("cat-type") as Category;
  const q = val("cat-q");
  renderSourceLegend(type);
  const box = el("cat-results");
  box.innerHTML = "";
  if (!q.trim()) {
    const d = document.createElement("div");
    d.className = "catempty";
    d.textContent = "Type to search the connected catalog. Green dots are live sources; hollow are vetted and queued.";
    box.appendChild(d);
    return;
  }
  const results = searchCatalog(q, type, 6);
  if (results.length === 0) {
    const d = document.createElement("div");
    d.className = "catempty";
    d.textContent = "No matches in the connected sources yet.";
    box.appendChild(d);
    return;
  }
  for (const r of results) {
    const row = document.createElement("div");
    row.className = "result";
    const sw = document.createElement("div");
    sw.className = "sw";
    sw.style.background = r.hex ?? "#888";
    const info = document.createElement("div");
    info.className = "info";
    const nm = document.createElement("div");
    nm.className = "nm";
    nm.textContent = r.name;
    const meta = document.createElement("div");
    meta.className = "meta";
    meta.textContent = r.detail;
    info.appendChild(nm);
    info.appendChild(meta);
    const badge = document.createElement("span");
    badge.className = "srcbadge";
    badge.textContent = `${r.sourceName} · T${r.tier}`;
    const btn = document.createElement("button");
    btn.textContent = "Use in plan";
    btn.onclick = () => {
      if (r.category === "paint") picks.wall = r;
      else picks.floor = r;
      render();
      el("catCard").scrollIntoView({ block: "nearest" });
    };
    row.appendChild(sw);
    row.appendChild(info);
    row.appendChild(badge);
    row.appendChild(btn);
    box.appendChild(row);
  }
}

function tempWord(t: number): string {
  return t > 0.12 ? "warm" : t < -0.12 ? "cool" : "neutral";
}

// Approximate a swatch from a published LRV. LRV is essentially percent
// luminance, so we invert the sRGB transfer curve to get a neutral of that
// lightness, then tint slightly by undertone. Display only: every rule the
// engine runs uses the LRV and temperature numbers, never this hex.
function lrvToHex(lrv: number, temp: number): string {
  const Y = Math.max(0, Math.min(1, lrv / 100));
  const enc = Y <= 0.0031308 ? 12.92 * Y : 1.055 * Math.pow(Y, 1 / 2.4) - 0.055;
  const base = Math.round(Math.max(0, Math.min(1, enc)) * 255);
  const warmth = Math.round(temp * 14);
  const to2 = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${to2(base + warmth)}${to2(base)}${to2(base - warmth)}`;
}

// Apply a user-entered color as the wall pick. Name + LRV is the whole
// requirement; undertone is optional because manufacturers publish LRV
// everywhere and undertone almost nowhere.
function applyOwnColor() {
  const name = (val("own-name") || "").trim();
  const lrvRaw = val("own-lrv");
  const note = el("own-note");
  const lrv = Number(lrvRaw);

  if (!name || lrvRaw === "" || Number.isNaN(lrv) || lrv < 0 || lrv > 100) {
    note.className = "ownnote warn";
    note.textContent = "Enter a color name and an LRV between 0 and 100 (it's on the manufacturer's product page).";
    return;
  }
  const tempRaw = val("own-temp");
  const temperature = tempRaw === "" ? 0 : Number(tempRaw);

  picks.wall = {
    sourceId: "user", sourceName: "Your color", tier: 1, category: "paint",
    id: "custom", name, hex: lrvToHex(lrv, temperature), lrv,
    temperature, chroma: 0.15,
    undertone: tempRaw === "" ? "unspecified" : tempWord(temperature),
    detail: `LRV ${lrv}`,
  };
  note.className = "ownnote";
  note.textContent = tempRaw === ""
    ? `Using ${name} at LRV ${lrv}. Add an undertone later if you want a sharper harmony check against the floor.`
    : `Using ${name} at LRV ${lrv}, ${tempWord(temperature)}.`;
  render();
}

// Render the note under a selectable control. Silent by default, and the
// rule of thumb is shown ONLY once the user has moved this control: if the
// engine chose it, it already satisfies the engine's own rules, so a tip
// there would be noise arguing with itself. Tips respond to your selection.
function renderNote(
  id: string, tip: Tip | null, changed: boolean, resetLabel: string, onReset: () => void
) {
  const node = el(id);
  node.innerHTML = "";
  const shown = changed ? tip : null;
  node.className = "statrec" + (shown?.level === "caution" ? " warn" : "");
  if (!changed) return; // the quiet, default case

  const a = document.createElement("a");
  a.textContent = resetLabel;
  a.onclick = onReset;
  node.appendChild(a);
  if (shown) {
    const t = document.createElement("span");
    t.className = "tip";
    t.textContent = shown.text;
    node.appendChild(t);
  }
}

// Representative swatch color per finish name, for the little dot in the
// Hardware Plan card. Illustrative only; the actual decision runs on
// metal_family, never on this hex.
const FINISH_HEX: Record<string, string> = {
  "champagne-bronze": "#C6A876", "brass": "#C7A03E", "gold": "#D4AF37",
  "copper": "#B87333", "oil-rubbed-bronze": "#4A3728",
  "chrome": "#C9CDD1", "polished-nickel": "#C6C2B6", "brushed-nickel": "#B8BCC0", "stainless": "#B7BBBE",
  "matte-black": "#2B2B2B", "graphite": "#4B4B4B", "pewter": "#8E8C84",
};

function renderHardwareCard(hw: ReturnType<typeof planHardware>) {
  el("hw-family").textContent = `${hw.metalFamily.charAt(0).toUpperCase()}${hw.metalFamily.slice(1)} metals`;
  const box = el("hw-picks");
  box.innerHTML = "";
  for (const p of hw.picks) {
    const row = document.createElement("div");
    row.className = "hwpick";
    const fin = document.createElement("div");
    fin.className = "fin";
    fin.style.background = FINISH_HEX[p.finish] ?? "#888";
    const info = document.createElement("div");
    info.className = "info";
    const cat = document.createElement("div");
    cat.className = "cat";
    cat.textContent = p.category;
    const nm = document.createElement("div");
    nm.className = "nm";
    nm.textContent = `${p.name} (${p.finish})`;
    info.appendChild(cat);
    info.appendChild(nm);
    const price = document.createElement("div");
    price.className = "price";
    price.textContent = `$${p.price}`;
    row.appendChild(fin);
    row.appendChild(info);
    row.appendChild(price);
    box.appendChild(row);
  }
  bullets("hw-why", hw.reasoning);
}

function render() {
  renderTabs();
  const r = preset();
  const length = Number(val("length")) || r.length;
  const width = Number(val("width")) || r.width;
  const sqft = Math.round(length * width);
  const ceiling = Number(val("ceiling")) || r.ceiling;
  const orient = val("orient") as Orientation;
  const daylight = val("daylight") as DaylightLevel;
  const objective = val("objective") as Objective;
  const style = val("style") as StyleKey;
  const stylePull = Number(val("pull")) || 0.25;
  const tone = val("tone") as Tone;

  // Lighting (engine core): brightness and fixture-count overrides feed the
  // lumen math and grid layout inside planLighting, so lumens/spacing/per-
  // fixture recompute consistently.
  const lightIn: LightingInput = {
    roomType: r.roomType, sqft, ceilingHeightFt: ceiling, orientation: orient, daylight, objective,
    roomLengthFt: length, roomWidthFt: width,
    targetFcOverride: fcOverride ?? undefined,
    fixtureCountOverride: countOverride ?? undefined,
  };
  const L = planLighting(lightIn);

  // Active CCT = the room's recommendation, unless the user moved the slider.
  const activeCCT = cctOverride ?? L.cct;
  el("l-cct").textContent = activeCCT + "K";
  el("l-cctlabel").textContent = cctLabel(activeCCT);
  el("l-fc").innerHTML = `${L.targetFc} fc <small>foot-candles</small>`;
  el("l-lumens").innerHTML = `${L.ambientLumens.toLocaleString()} <small>lm</small>`;
  el("l-cans").innerHTML = `${L.fixtureCount} <small>at ~${L.lumensPerFixture} lm</small>`;
  el("l-spacing").innerHTML = `${L.spacingFt} ft <small>/ ${L.wallOffsetFt} ft</small>`;

  // Brightness + fixture-count sliders (the recompute is visible in lumens /
  // spacing above). Each shows a recommended value and a reset link.
  (el("fc-slider") as HTMLInputElement).value = String(L.targetFc);
  renderNote("l-fc-rec",
    brightnessTip(L.targetFc, L.recommendedTargetFc, r.roomType),
    fcOverride !== null && fcOverride !== L.recommendedTargetFc,
    `reset to ${L.recommendedTargetFc} fc`,
    () => { fcOverride = null; render(); });

  (el("cans-slider") as HTMLInputElement).value = String(L.fixtureCount);
  renderNote("l-cans-rec",
    fixtureTip(L.fixtureCount, L.recommendedFixtureCount),
    countOverride !== null && countOverride !== L.recommendedFixtureCount,
    `reset to ${L.recommendedFixtureCount}`,
    () => { countOverride = null; render(); });

  // Kelvin slider: position it at the active CCT; the green band marks the
  // room's recommended range; the note lets the user snap back.
  (el("cct-slider") as HTMLInputElement).value = String(activeCCT);
  const span = 5000 - 2700;
  const lo = Math.max(0, Math.min(1, (L.cctBand[0] - 2700) / span)) * 100;
  const hi = Math.max(0, Math.min(1, (L.cctBand[1] - 2700) / span)) * 100;
  el("l-krange").style.left = lo + "%";
  el("l-krange").style.width = Math.max(3, hi - lo) + "%";
  renderNote("l-cct-rec",
    cctTip(activeCCT, r.roomType),
    cctOverride !== null && cctOverride !== L.cct,
    `reset to ${L.cct}K`,
    () => { cctOverride = null; render(); });

  el("l-viz").innerHTML = roomViz(L.cols, L.rows, L.spacingFt, L.wallOffsetFt);
  el("l-layers").innerHTML = L.layers
    .map((x) => `<div class="layer"><span class="dot" style="background:${LAYER_COLOR[x.kind]}"></span><span class="lbl">${x.label}</span><span class="val">${x.detail}</span></div>`)
    .join("");
  bullets("l-why", L.reasoning);

  // Flooring resolves FIRST: it anchors the palette, and its temperature is
  // the constraint the wall color must harmonize with (W-01). A pulled-in
  // floor override changes that temperature too.
  const F = planFloor({
    roomType: r.roomType, orientation: orient, bulbCCT: activeCCT, objective, sqft,
    styleVibe: style, stylePull,
    targetLrv: floorLrvOverride ?? undefined,
    categoryFilter: (floorCatOverride as FloorCategory | null) ?? undefined,
  });
  const baselineF = floorLrvOverride === null ? F : planFloor({
    roomType: r.roomType, orientation: orient, bulbCCT: activeCCT, objective, sqft,
    styleVibe: style, stylePull,
    categoryFilter: (floorCatOverride as FloorCategory | null) ?? undefined,
  });
  const floorTemperature = picks.floor ? (picks.floor.temperature ?? F.top.temperature) : F.top.temperature;

  // Paint (engine core): judged under the ACTIVE CCT, coordinated with the
  // floor via W-01, steered by vibe. A target LRV steers the color toward a
  // lightness; a sheen override sets the sheen.
  const paintIn: PaintPlanInput = {
    roomType: r.roomType, orientation: orient, bulbCCT: activeCCT, daylight, objective, sqft,
    floorTemperature, styleVibe: style, stylePull,
    targetLrv: lrvOverride ?? undefined,
    sheenOverride: (sheenOverride as PaintPlanInput["sheenOverride"]) ?? undefined,
  };
  const P = planWallColor(paintIn);
  const baselineP = lrvOverride === null && sheenOverride === null ? P : planWallColor({
    ...paintIn,
    targetLrv: undefined,
    sheenOverride: undefined,
  });
  const w = P.wall;

  renderDesignLab(L, P, F, baselineP, baselineF);

  // Confidence + the real W-01 harmony (finding: the app now surfaces what
  // only the demo used to).
  const metaBits = [`Confidence <b>${P.confidence}</b>`];
  if (P.harmony) metaBits.push(`W-01 harmony <b>${P.harmony.score}</b> <span>(gap ${P.harmony.gap})</span>`);
  if (P.hardFilteredCount > 0) metaBits.push(`<b>${P.hardFilteredCount}</b> filtered for clashing with the floor`);
  el("p-meta").innerHTML = picks.wall ? "" : metaBits.map((b) => `<span>${b}</span>`).join("");

  el("p-name").textContent = w.name;
  el("p-line").textContent = `${w.brand} ${w.code} in ${P.line.name}`;
  el("p-swatch").style.background = w.hex;
  el("p-code").textContent = `${w.name} ${w.code}`;
  el("p-mood").textContent = P.mood.charAt(0).toUpperCase() + P.mood.slice(1) + " palette";
  el("p-lrv").innerHTML = `${w.lrv} <small>/ 100</small>`;
  el("p-under").innerHTML = `${w.undertone} <small>${w.tempLabel}</small>`;
  el("p-sheen").innerHTML = `${P.wallSheen}${P.steppedDown ? ` <small>stepped down from ${P.baseSheen}</small>` : ""}`;
  el("p-trim").innerHTML = `${P.trimSheen} <small>S-01</small>`;
  el("p-alt-sw").style.background = P.runnerUp.hex;
  el("p-alt").textContent = `${P.runnerUp.name} ${P.runnerUp.code} (LRV ${P.runnerUp.lrv})`;
  bullets("p-why", P.reasoning);

  // Target-LRV slider: sits at the current pick's LRV; the note lets the user
  // snap back to letting the mood choose the lightness.
  (el("lrv-slider") as HTMLInputElement).value = String(Math.round(w.lrv));
  // A pulled-in or hand-entered color is a user choice too, so it earns a tip.
  const effWallLrvForTip = picks.wall ? (picks.wall.lrv ?? w.lrv) : w.lrv;
  renderNote("p-lrv-rec",
    lrvTip(effWallLrvForTip, P.lrvFloor, r.roomType),
    P.targetLrv !== null || picks.wall != null,
    picks.wall ? "back to the engine's pick" : "let the engine pick",
    () => { lrvOverride = null; picks.wall = undefined; render(); });

  (el("sheen-select") as HTMLSelectElement).value = P.wallSheen;
  renderNote("p-sheen-rec",
    sheenTip(P.wallSheen, r.roomType, P.sheenBelowWetFloor),
    sheenOverride !== null && sheenOverride !== P.recommendedSheen,
    `reset to ${P.recommendedSheen}`,
    () => { sheenOverride = null; render(); });

  // Effective wall = recommendation, unless the user pulled one from search.
  let wallChroma = w.chroma;
  let wallInsightAttrs = { name: w.name, lrv: w.lrv, temperature: w.effTemp, chroma: w.chroma };
  if (picks.wall) {
    const pw = picks.wall;
    const effT = effectiveTemperature(pw.temperature ?? 0, orient, activeCCT);
    const ownColor = pw.sourceId === "user";
    el("p-name").textContent = pw.name;
    el("p-line").textContent = ownColor
      ? `Your color · LRV ${pw.lrv}${pw.undertone && pw.undertone !== "unspecified" ? ` · ${pw.undertone}` : ""}`
      : `${pw.sourceName} ${pw.id} · pulled from search`;
    el("p-swatch").style.background = pw.hex ?? "#888";
    el("p-code").textContent = `${pw.name} ${pw.id}`;
    el("p-mood").textContent = `Your pick · ${pw.sourceName}`;
    el("p-lrv").innerHTML = `${pw.lrv ?? "-"} <small>/ 100</small>`;
    el("p-under").innerHTML = `${pw.undertone ?? "-"} <small>${tempWord(effT)}</small>`;
    bullets("p-why", [
      ownColor
        ? `Your own color at LRV ${pw.lrv}. LRV is all the engine needs for the light and livability checks${pw.undertone === "unspecified" ? "; without an undertone the harmony check is approximate" : ""}.`
        : `Pulled from ${pw.sourceName} (tier ${pw.tier}) via catalog search.`,
      `Sheen and trim still follow the room and rule S-01: ${P.wallSheen} walls, ${P.trimSheen} trim.`,
      `Style insight below is recomputed for your pick, judged under ${activeCCT}K light.`,
    ]);
    wallChroma = pw.chroma ?? w.chroma;
    wallInsightAttrs = { name: pw.name, lrv: pw.lrv ?? 0, temperature: effT, chroma: pw.chroma ?? 0 };
  }

  // Style insight on the effective wall pick (threshold-gated), in the tone
  showInsight("p-insight", styleInsight(wallInsightAttrs, style, "wall"), tone);

  // Flooring display (F was resolved above, before paint, so W-01 could read it).
  const f = F.top;
  el("f-name").textContent = f.name;
  el("f-line").textContent = `${f.category} | ${f.stain} | ${f.install} install`;
  (el("f-swatch") as HTMLElement).style.background = f.hex;
  el("f-cat").textContent = f.category.toUpperCase();
  el("f-tex").textContent = f.texture + (f.refinishable ? ", refinishable" : "");
  el("f-lrv").innerHTML = `${f.lrv} <small>/ 100</small>`;
  el("f-material").textContent = f.category;
  el("f-water").innerHTML = f.water_resistant ? "yes" : "no";
  el("f-price").innerHTML = `$${f.price_per_sqft.toFixed(2)} <small>/sqft · ${f.install}</small>`;
  bullets("f-why", F.reasoning);

  // Target-LRV slider for the floor (same pattern as the paint card).
  (el("flrv-slider") as HTMLInputElement).value = String(Math.round(f.lrv));
  renderNote("f-lrv-rec",
    floorLrvTip(picks.floor ? (picks.floor.lrv ?? f.lrv) : f.lrv),
    F.targetLrv !== null || picks.floor != null,
    picks.floor ? "back to the engine's pick" : "let the engine pick",
    () => { floorLrvOverride = null; picks.floor = undefined; render(); });

  // Material filter: only categories that actually suit this room.
  const catSel = el("fcat-select") as HTMLSelectElement;
  catSel.innerHTML = "";
  const anyOpt = document.createElement("option");
  anyOpt.value = "";
  anyOpt.textContent = "any material";
  catSel.appendChild(anyOpt);
  for (const c of F.availableCategories) {
    const o = document.createElement("option");
    o.value = c;
    o.textContent = c;
    catSel.appendChild(o);
  }
  catSel.value = floorCatOverride ?? "";
  renderNote("f-cat-rec", null, floorCatOverride !== null, "show all materials",
    () => { floorCatOverride = null; render(); });

  // Populate the floor picker with room-appropriate options and default to
  // the recommendation, then compute its insight.
  floorCtx = { orient, cct: activeCCT, style, tone, floors: F.options.map((o) => o.floor) };
  const sel = el("floor-picker") as HTMLSelectElement;
  sel.innerHTML = "";
  for (const o of F.options) {
    const opt = document.createElement("option");
    opt.value = o.floor.name;
    opt.textContent = `${o.floor.name} - LRV ${o.floor.lrv}`;
    sel.appendChild(opt);
  }
  sel.value = f.name;
  renderFloorSelection(f.name);

  // Effective floor = recommendation, unless the user pulled one from search.
  let effFloor = { name: f.name, category: f.category as string, temperature: f.temperature, chroma: f.chroma_opinion };
  if (picks.floor) {
    const pf = picks.floor;
    const cat = pf.sub ?? "flooring";
    el("f-name").textContent = pf.name;
    el("f-line").textContent = `${pf.sourceName} · pulled from search`;
    (el("f-swatch") as HTMLElement).style.background = pf.hex ?? "#888";
    el("f-cat").textContent = cat.toUpperCase();
    el("f-tex").textContent = "your pick";
    el("f-lrv").innerHTML = `${pf.lrv ?? "-"} <small>/ 100</small>`;
    el("f-material").textContent = cat;
    el("f-water").innerHTML = "-";
    el("f-price").innerHTML = pf.price != null ? `$${pf.price.toFixed(2)} <small>/sqft</small>` : "-";
    bullets("f-why", [
      `Pulled from ${pf.sourceName} (tier ${pf.tier}) via catalog search.`,
      `Its temperature still feeds the wall harmony check and the coherence lens below.`,
    ]);
    effFloor = { name: pf.name, category: cat, temperature: pf.temperature ?? 0, chroma: pf.chroma ?? 0.2 };
    const effT = effectiveTemperature(effFloor.temperature, orient, activeCCT);
    showInsight("f-insight", styleInsight({ name: pf.name, lrv: pf.lrv ?? 0, temperature: effT, chroma: effFloor.chroma }, style, "floor"), tone);
    el("f-fit").textContent = "";
  }

  // Hardware (engine core), steered by vibe: one coordinated metal family
  // for the room, then the best piece per category within it.
  const HW = planHardware({ roomType: r.roomType, objective, styleVibe: style, stylePull });
  renderHardwareCard(HW);

  // Coherence base from the EFFECTIVE picks (recommendation or pulled-in),
  // PLUS the real hardware plan. This is the actual room, not a toy: with
  // no clutter toggles on, it should verdict clean because the hardware
  // engine already coordinated the metal family before this ever ran.
  const floorIsWood = ["wood", "laminate", "lvp"].includes(effFloor.category);
  const base: FinishItem[] = [
    {
      role: "floor", label: effFloor.name,
      woodTone: floorIsWood ? { label: effFloor.name, temperature: effFloor.temperature } : undefined,
      chroma: effFloor.chroma, areaWeight: 0.5,
    },
    { role: "wall", label: `${picks.wall ? picks.wall.name : w.name} walls`, chroma: wallChroma, areaWeight: 0.6 },
    ...HW.picks.map((p) => ({ role: p.category, label: p.name, metalFamily: p.metalFamily })),
  ];
  cohCtx = { base, tone };

  // Design Score inputs: assembled from what the engines already computed,
  // using the EFFECTIVE picks so catalog pull-ins and overrides count too.
  const effWallLrv = picks.wall ? (picks.wall.lrv ?? w.lrv) : w.lrv;
  scoreCtx = {
    styleVibe: style,
    styleLabel: STYLES[style]?.label ?? style,
    wall: {
      name: picks.wall ? picks.wall.name : w.name,
      lrv: effWallLrv,
      effTemp: wallInsightAttrs.temperature,
      chroma: wallChroma,
    },
    floor: {
      name: effFloor.name,
      lrv: picks.floor ? (picks.floor.lrv ?? F.top.lrv) : F.top.lrv,
      effTemp: effectiveTemperature(effFloor.temperature, orient, activeCCT),
      chroma: effFloor.chroma,
    },
    harmony: P.harmony,
    lrvFloor: P.lrvFloor,
    wallMeetsLrvFloor: P.lrvFloor === 0 || effWallLrv >= P.lrvFloor,
    sheenBelowWetFloor: P.sheenBelowWetFloor,
    durabilityOk: P.line.durability_class === "A",
  };

  renderCoherenceCard(); // also renders the Design Score
}

(el("floor-picker") as HTMLSelectElement).addEventListener("change", (e) => {
  renderFloorSelection((e.target as HTMLSelectElement).value);
});

// Kelvin slider: the first user-selectable engine factor. Dragging it
// overrides the recommended CCT and re-runs the paint color math under it.
el("cct-slider").addEventListener("input", (e) => {
  cctOverride = Number((e.target as HTMLInputElement).value);
  render();
});
// Brightness + fixture count: override into planLighting, which recomputes
// lumens, spacing, and per-fixture output.
el("fc-slider").addEventListener("input", (e) => {
  fcOverride = Number((e.target as HTMLInputElement).value);
  render();
});
el("cans-slider").addEventListener("input", (e) => {
  countOverride = Number((e.target as HTMLInputElement).value);
  render();
});
// Paint: target LRV steers the color pick; sheen select overrides the sheen.
el("lrv-slider").addEventListener("input", (e) => {
  lrvOverride = Number((e.target as HTMLInputElement).value);
  render();
});
el("sheen-select").addEventListener("change", (e) => {
  sheenOverride = (e.target as HTMLSelectElement).value;
  render();
});
// Floor: target lightness + material-family filter.
el("flrv-slider").addEventListener("input", (e) => {
  floorLrvOverride = Number((e.target as HTMLInputElement).value);
  render();
});
el("fcat-select").addEventListener("change", (e) => {
  const v = (e.target as HTMLSelectElement).value;
  floorCatOverride = v === "" ? null : v;
  render();
});

// Fixture layout: kept out of the way behind a toggle rather than always on.
el("l-viz-toggle").addEventListener("click", () => {
  const viz = el("l-viz");
  const open = viz.classList.toggle("hidden") === false;
  el("l-viz-toggle").textContent = open ? "Hide fixture layout" : "Show fixture layout";
  el("l-viz-toggle").setAttribute("aria-expanded", String(open));
});

for (const id of ["length", "width", "ceiling", "orient", "daylight", "objective", "style", "pull", "tone"]) {
  el(id).addEventListener("input", render);
  el(id).addEventListener("change", render);
}
el("own-apply").addEventListener("click", applyOwnColor);
el("own-lrv").addEventListener("keydown", (e) => {
  if ((e as KeyboardEvent).key === "Enter") applyOwnColor();
});
el("cat-q").addEventListener("input", runSearch);
el("cat-type").addEventListener("change", runSearch);

initCoherenceToggles();
(el("lab-reset") as HTMLButtonElement).onclick = resetDesignLevers;
syncControls();
render();
runSearch();
